import {
    CombineOperator,
    CreateOrganizationRequest,
    EmailCaptureWithTopic,
    FeatureTierLevel,
    FeatureType,
    FilterType,
    GetFolderResponse,
    GetPointResponse,
    GetTopicResponse,
    ImageWithType,
    KeywordSearch,
    SearchFilter,
    TopicSearchResult,
    KlaviyoPropertyKey,
} from '../../types';
import { LitElement, css, html, unsafeCSS } from 'lit';
import { checkIfAIEligible, redirectToLogin, saveEmail, setAccount } from '../../helpers/api/account-api';
import { getBookmarksforBackpack, getContributedToTopics, getPaginatedTopicsforBackpack, getPersonalTopicsforBackpack, getTopics, getTopicsforBackpack } from '../../helpers/api/topics-api';
import { createOrganization, migrateTopicToOrganization } from '../../helpers/api/organization-api';
import { customElement, property, query, state } from 'lit/decorators';
import { getFromLocalStorage, isDefined, removeLocalStorage, setLocalStorage, undefinedIfEmpty } from '../../helpers/utils';

import { AddEditFolderModal } from '../../components/add-edit-folder-modal';
import { BackpackSidebar } from '../../components/backpack-sidebar';
import { Dialog } from '@material/mwc-dialog';
import { EditCreatorProfilePage } from './edit-creator-profile-page';
import { STOP_WORDS } from '../../helpers/constants';
import { SettingDrawer } from '../../components/mobile/setting-drawer';
import { StateController } from '../../helpers/state/state-management/state-controller';
import { TextArea } from '@material/mwc-textarea';
import accountInfo from '../../helpers/state/account-info';
import { detectMobileViewport } from '../../helpers/device';
import featuresInfo from '../../helpers/state/features-info';
import { getFilteredTopics } from '../../helpers/topics-backpack';
import { getFolders } from '../../helpers/api/folders-api';
import { getMainPage } from '../../helpers/page-name';
import { getQueryStringParameters } from '../../helpers/urls';
import { getRandomString } from '../../helpers/strings';
import { getVisitedPoints } from '../../helpers/api/points-api';
import { heights } from '../../style/variables';
import { logWithAmplitude, logPropertiesWithKlayvio } from '../../helpers/api/api';
import { getNewUiUrl, isCustomDomain, isLegacyAccount, redirectToNewUiWithTransfer, resolveUiForAccount, setUiPref } from '../../helpers/auth-cookie';
import { openChatWindow } from '../../helpers/intercom';
import organizationInfo from '../../helpers/state/organization-info';
import subscriptionInfo from '../../helpers/state/subscription-info';
import { textToMapAI } from '../../helpers/api/gpt-api';

@customElement('backpack-page')
export class BackpackPage extends LitElement {
    static amplitude_callsite: string = 'Backpack Page';

    @query('#manageAccessComponent')
    manageAccessComponent?: Dialog;

    @query('#addRenameModalContent')
    addRenameModalContentElement?: AddEditFolderModal;

    @query('#upgradeDialogComponent')
    upgradeDialogComponent!: Dialog;

    @query('#customAdvancedInsightsModal')
    customAdvancedInsightsModal?: Dialog;

    @query ('#editCreatorProfilePage')
    editCreatorProfilePage?: EditCreatorProfilePage;

    @query('#textToMapArea')
    private readonly textToMapAreaElement?: TextArea;

    @query('#promptArea')
    private readonly promptAreaElement?: TextArea;

    @query('#addToMapModal')
    addToMapModal!: Dialog;

    @query('#addToMapDrawer')
    addToMapDrawer?: SettingDrawer;

    @query('#sidebarComponent')
    sidebarComponent?: BackpackSidebar;

    @query('#organizationProfilePopupModal')
    organizationProfilePopupModal?: Dialog;

    @property({ type: Array })
    folders: GetFolderResponse[] = [];

    @property({ type: Array })
    visitedPoints: GetPointResponse[] = [];

    @property({ type: Object })
    topic: GetTopicResponse | undefined;

    @property({ type: String })
    crowdsourceLink?: string;

    @property({ type: String })
    viewonlyLink?: string;

    @property()
    featureType?: FeatureType;

    @property({ type: Array})
    flattened_emails: EmailCaptureWithTopic[] = [];

    @property({ type: Array})
    emailWithTopic: EmailCaptureWithTopic[] = [];

    @property({ type: String })
    shareLink: string | undefined;

    @property()
    readEmbed?: string;

    @property()
    addPointEmbed?: string;

    @property()
    searchTagString: string = '';

    @property({ type: Array })
    mapsForYou: TopicSearchResult[] = [];

    @property()
    analyticsURL: string = '';

    @property({type: Boolean})
    ai_eligible: boolean = true;

    @state()
    topics: TopicSearchResult[] = [];

    @state()
    personal_topics: TopicSearchResult[] = [];

    @state()
    bookmarks: TopicSearchResult[] = [];

    @state()
    contributedTopics: TopicSearchResult[] = [];

    @state()
    insightTopics: TopicSearchResult[] = [];

    @state()
    filterType?: string;

    @state()
    viewType?: string = 'list';

    @state()
    profileId?: string;

    @state()
    showAdvancedInsightsModal: boolean = false;

    @state()
    mobileMinified: boolean = detectMobileViewport();

    @state()
    publishedTopics: GetTopicResponse[] = [];

    @state()
    isBackpackMapsLoading: boolean = true;

    @state()
    isInsightsLoading: boolean = true;

    @state()
    isMapsForYouLoading: boolean = true;

    @state()
    isVisitedPlacesLoading: boolean = true;

    @state()
    showEmailDownload: boolean = false;

    @state()
    currentTabIndex: number = 0;

    @state()
    ttmIsLoading: boolean = false;

    @state()
    imageUrls: ImageWithType[][] = [];

    @state()
    ttmError: boolean = false;

    @state()
    currentPageName: string = 'Home';

    @state()
    newMapLoading: boolean = false;

    @state()
    runningMigration: boolean = false;

    @state()
    loading: boolean = false;

    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        await this.startup();
    }

    @state()
    showNewUiBanner: boolean = false;

    private async startup() {
        if (!accountInfo.isAuthenticated) {
            redirectToLogin();
            console.error('User is not authenticated');
            return;
        }

        // Check UI preference — redirect new accounts or users who chose the new UI
        const accountId = accountInfo.id?.$oid;
        if (accountId) {
            const uiPref = resolveUiForAccount(accountId);
            if (uiPref === 'new') {
                if (isCustomDomain()) {
                    // Custom domains can't share cookies with *.proxi.co —
                    // use a one-time transfer token to carry auth across.
                    void redirectToNewUiWithTransfer();
                } else {
                    window.location.href = getNewUiUrl();
                }
                return;
            }
            // Legacy account on classic UI — show banner offering the new UI
            if (isLegacyAccount(accountId)) {
                this.showNewUiBanner = true;
            }
        }

        // Track that this user is on the classic interface
        logPropertiesWithKlayvio(accountInfo.email, {
            [KlaviyoPropertyKey.ActiveInterface]: 'classic',
        });

        // run for people who log themselves into personal
        if (!organizationInfo.organization && accountInfo.migrated_from_personal !== true) {
            this.runningMigration = true;
            // if the user is not logged into an org, then we need to migrate them
            await this.runTeamMigration();
        }

        const queryString = getQueryStringParameters();

        const addingToModal = queryString.get('addToModal');
        if (addingToModal) {
            this.addToMapModal.open = true;
        }

        // look for page_name in query string
        const pageName = queryString.get('page_name');
        if (pageName) {
            this.currentPageName = pageName;
        }

        this.filterType = queryString.get('filter_type');
        const setting_page = queryString.get('settings');
        if (setting_page) {
            if (setting_page === 'team' && organizationInfo.organization?.is_administrator === true) {
                this.currentPageName = 'Team Settings';
            } else if (setting_page === 'account') {
                this.currentPageName = 'Account Settings';
            }
        }

        // Initialize arrays that will be populated progressively
        this.topics = [];
        this.personal_topics = [];
        this.bookmarks = [];
        this.contributedTopics = [];
        this.folders = [];
        this.ai_eligible = true;

        // Start progressive loading of organization topics immediately
        const topicsPromise = getPaginatedTopicsforBackpack(
            BackpackPage.amplitude_callsite,
            (batch) => {
                // Filter out template and append each batch as it arrives
                const filteredBatch = batch.filter((topic) => topic._id !== organizationInfo.organization?.default_map_template_id);
                this.topics = [...this.topics, ...filteredBatch];
                this.insightTopics = this.topics;

                // Hide loading spinner after first batch to show progressive results
                if (this.isBackpackMapsLoading) {
                    this.isBackpackMapsLoading = false;
                }

                this.requestUpdate();
            },
            (totalTopics) => {
                // Ensure loading is off when complete (in case no batches arrived)
                this.isBackpackMapsLoading = false;
                this.requestUpdate();
            }
        );

        // Load other data in parallel
        try {
            const defaultValues = [[], [], [], true];
            this.loading = true;
            const results = await Promise.allSettled([
                getPersonalTopicsforBackpack(BackpackPage.amplitude_callsite),
                getBookmarksforBackpack(BackpackPage.amplitude_callsite),
                getContributedToTopics(BackpackPage.amplitude_callsite),
                getFolders(BackpackPage.amplitude_callsite),
                checkIfAIEligible(BackpackPage.amplitude_callsite)
            ]);

            const [personalTopics, bookmarks, contributedTopics, folders, ai_eligible] = results.map((result, index) =>
                result.status === 'fulfilled' ? result.value : defaultValues[index]
            ) as [GetTopicResponse[], GetTopicResponse[], GetTopicResponse[], GetFolderResponse[], boolean];

            this.personal_topics = personalTopics;
            this.bookmarks = bookmarks;
            this.contributedTopics = contributedTopics;
            this.folders = folders;
            this.ai_eligible = ai_eligible;
            this.loading = false;
            this.requestUpdate();
        } catch (error) {
            console.error('Failed to get backpack data', error);
            this.loading = false;
        }
        window.dispatchEvent(new CustomEvent('page-content-ready'));

        // Ensure topics loading completes
        await topicsPromise;
        // Ensure that local storage, amplitude, and chameleon have an email if possible
        // Because we are constantly adding new tools that require login, we should always run this to make sure all are updated
        const local_storage_email = getFromLocalStorage('email');
        if (accountInfo.isAuthenticated) {
            saveEmail(accountInfo.email);
        } else if (local_storage_email !== null) {
            saveEmail(local_storage_email);
        }

        // TEMPORARY: If user hasn't been migrated yet, is on an organization, and has no personal topics, say that they are migrated
        if (accountInfo.migrated_from_personal !== true && isDefined(organizationInfo.organization) && this.personal_topics.length === 0) {
            const account = accountInfo.account;
            if (account) {
                account.migrated_from_personal = true;
                await setAccount('migration', account);
            }
        }

        // Mark insights as ready once topics have loaded
        this.isInsightsLoading = false;
        this.requestUpdate();

        await this.getVisits();
        this.isVisitedPlacesLoading = false;
        void this.getTagsforDiscoverSearch();


        if (subscriptionInfo.subscription_tier !== FeatureTierLevel.Teams) {
            removeLocalStorage('migration_complete');
        }

        // Show organization profile popup if not completed yet
        this.maybeShowOrganizationProfilePopup();
    }

    private maybeShowOrganizationProfilePopup() {
        // Only show if user is an administrator and hasn't filled out the profile
    if (organizationInfo.organization?.is_administrator === true && !detectMobileViewport()) {
            const orgId = organizationInfo.organization._id.$oid;
            const completedKey = `org_profile_completed_${orgId}`;
            const completedValue = getFromLocalStorage(completedKey);

            // Check if completed permanently or if month has passed since skipping
            if (completedValue === 'permanent') {
                return; // User filled out the form, never show again
            }

            if (completedValue && completedValue !== 'permanent') {
                // Check if a month has passed since they skipped
                const skippedTimestamp = parseInt(completedValue, 10);
                const oneMonthAgo = Date.now() - (30 * 24 * 60 * 60 * 1000); // 30 days
                if (skippedTimestamp > oneMonthAgo) {
                    return; // Less than a month has passed, don't show
                }
            }

            const hasDemographics = isDefined(organizationInfo.organization.demographics) &&
                                   ((isDefined(organizationInfo.organization.demographics.business_category) && organizationInfo.organization.demographics.business_category.length > 0) ??
                                    (isDefined(organizationInfo.organization.demographics.use_cases) &&
                                     organizationInfo.organization.demographics.use_cases.length > 0));

            // Show popup if they haven't filled demographics
            if (!isDefined(hasDemographics) || !hasDemographics) {
                // Delay slightly to ensure the dialog is rendered
                setTimeout(() => {
                    if (this.organizationProfilePopupModal) {
                        this.organizationProfilePopupModal.open = true;
                    }
                }, 500);
            }
        }
    }

    private closeOrganizationProfilePopup() {
        if (this.organizationProfilePopupModal) {
            this.organizationProfilePopupModal.open = false;
        }
        // Mark as skipped with timestamp, so we can show again in a month
        if (organizationInfo.organization) {
            const orgId = organizationInfo.organization._id.$oid;
            setLocalStorage(`org_profile_completed_${orgId}`, Date.now().toString());
        }
    }

    private async onUpdateTopics() {
        await this.startup();
    }

    private openUpgradeDialog(featureType?: FeatureType) {
        this.featureType = featureType;
        this.upgradeDialogComponent.open = true;
    }

    private onUpdateFilterType(e: CustomEvent) {
        if (isDefined(e.detail.filterTypeInfo)) {
            window.location.assign(e.detail.filterTypeInfo.url);
            this.filterType = e.detail.filterTypeInfo.filterType;
        } else {
            this.filterType = undefined;
            // remove filter_type parameter from url if it exists.
            const url = new URL(window.location.href);
            url.searchParams.delete('filter_type');
            window.history.replaceState({}, '', url.toString());
        }
    }


    private async setProfileId(e: CustomEvent) {
        this.profileId = e.detail.creatorProfileId;
        if (this.editCreatorProfilePage) {
            await this.editCreatorProfilePage.setUpWithUserProfile(e.detail.creatorProfileId);
        }
    }

    private openDiscover() {
        window.open(`${getNewUiUrl()}/discover`, '_blank');
    }

    private onUpdatePageNme(e: CustomEvent) {
        this.currentPageName = e.detail.pageName;
    }

    private async getTagsforDiscoverSearch() { // this should be put into topics-api at some point
        if (this.mapsForYou.length !== 0) {
            return;
        }

        // get google tags from visited points
        const tags = this.visitedPoints.map((point) => point.tag_info?.google_tags ?? []);
        // get point location search for each visited point
        const pointLocations = this.visitedPoints.map((point) => point.location.search ?? []);

        // combine tags into one list and remove duplicates
        const allTags = [...tags, ...pointLocations].flat();
        const uniqueTags = [...new Set(allTags)];

        // count occurrences of each tag
        const tagCounts: { [key: string]: number } = uniqueTags.reduce<{ [key: string]: number }>((acc, tag) => {
            const count = allTags.filter((t) => t === tag).length;
            acc[tag] = count;
            return acc;
        }, {});

        // sort tags by most commonly occurring and grab the top 5
        const sortedTags = Object.entries(tagCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([tag]) => tag)
            .slice(0, 5);

        // concatenate tags into a string for discover search
        this.searchTagString = sortedTags.join(' ');

        // try {
        //     await this.discoverSearch();
        //     if (this.mapsForYou.length > 0) {
        //         this.isMapsForYouLoading = false;
        //         this.requestUpdate();
        //     }
        // } catch (error) {
        //     console.error('no search maps');
        // }
    }

    private async runTeamMigration() {
        try {
            // double check that no organization_id is set
            if (isDefined(organizationInfo.organization)) {
                this.runningMigration = false;
                return;
            }
            // get backpack for individual user.
            const maps = await getTopicsforBackpack(BackpackPage.amplitude_callsite);
            let mapsToMigrate = maps.filter((map) => map.is_owner && !map.organization_id);

            // put maps to migrage in order by descending of last_updated
            mapsToMigrate = mapsToMigrate.sort((a, b) => {
                    return +b.updated_at - +a.updated_at;
                });

            // create new team with current subscription
            const name = `${undefinedIfEmpty(accountInfo.fullName) ?? accountInfo.email.split('@')[0]}'s Workspace`;
            const request: CreateOrganizationRequest  =  {
                name,
                chargebee_subscription: accountInfo.account?.chargebee_subscription,
                enterprise_billing: false,
                sso: `${getRandomString(8)}`,
                logo: undefined,
                primary_color:  undefined,
                secondary_color: undefined,
                default_map_template_id: undefined,
                cname: undefined,
                custom_font: undefined,
                demographics: undefined,
            };

            const organization_response = await createOrganization(BackpackPage.amplitude_callsite, request);

        // migrate maps that the user is an owner on that don't already have an organization ID
        // Co-editors on that map will automatically become guests
            for (let i = 0; i < mapsToMigrate.length; i += 10) {
                const promises = mapsToMigrate.slice(i, i + 10).map(async map => {
                    return await migrateTopicToOrganization(BackpackPage.amplitude_callsite, organization_response._id.$oid, map._id.$oid, )
                        .catch((error) => {
                            console.error(`Failed to migrate map ${map.name ?? ''}:`, error);
                            getMainPage().errorToastElement.showToast(`Failed to migrate map ${map.name ?? ''}`);
                        });
                });
                await Promise.all(promises);

                // Delay for a few milliseconds before the next iteration
                await new Promise(resolve => setTimeout(resolve, 100)); // 100 milliseconds delay
            }

            const new_account = accountInfo.account;
            if (new_account) {
                new_account.migrated_from_personal = true;
                await setAccount('migration', new_account);
            }

            getMainPage().errorToastElement.showToast(`Migration Complete! Redirecting...`);

            setLocalStorage('migration_complete', 'true');

            // Move to New Org
            await this.postOrganizationUpgrade(organization_response._id.$oid);

            // Run chameleon walkthrouhg.
        } catch (error) {
            getMainPage().errorToastElement.showToastAndThrow(`Failed to run team migration`);
        }
    }

    private async postOrganizationUpgrade(organization_id: string | undefined) {
        // set local storage
        if (!organization_id) {
            localStorage.removeItem('organization_id');
        } else {
            localStorage.setItem('organization_id', organization_id);
        }
        // refresh the page
        window.location.reload();
    }

    private async getVisits() {
        const topicsWithVisits: string[] = [];

        // Loop through each array of checkins in the map
        for (const checkinArray of accountInfo.account_checkins.values()) {
            // Loop through each checkin in the array
            for (const checkin of checkinArray) {
                if (isDefined(checkin.topic_id) && isDefined(checkin.topic_id.$oid)) {
                    topicsWithVisits.push(checkin.topic_id.$oid);
                }
            }
        }

        const uniqueTopicsWithVisits = [...new Set(topicsWithVisits)];

        // filter out topicIds that do not appear in this.topics
        const topicsWithVisitsInBackpack = uniqueTopicsWithVisits.filter(
            (topicId) => this.topics.some((topic) => topic._id.$oid === topicId)
        );

        const allVisitedPoints = await getVisitedPoints(
            BackpackPage.amplitude_callsite,
            topicsWithVisitsInBackpack
        );

        this.visitedPoints = allVisitedPoints.slice(0, 6) ?? [];
    }

    openTeammates() {
        if (organizationInfo.organization?.is_administrator === true) {
            window.location.href = '/#home?page_name=Team%20Settings&setting_type=ManageTeamMembers';
            this.currentPageName = 'Team Settings';
        } else {
            getMainPage().errorToastElement.showToast('Only workspace admins can manage team members');
        }
    }

    openSubscription() {
        if (organizationInfo.organization?.is_administrator === true) {
            window.location.href = '/#home?page_name=Team%20Settings&setting_type=SubscriptionAndBilling';
            this.currentPageName = 'Team Settings';
        } else {
            getMainPage().errorToastElement.showToast('Only workspace admins can manage the subscription');
        }
    }

    async openWorkspaceDropdown() {
        await this.sidebarComponent?.openWorkspaceDropdown();
    }

    private async discoverSearch() {
        let searchFilter: SearchFilter = {};

        const keywords = this.searchTagString
            .split(' ')
            .filter((search_word) => !STOP_WORDS.includes(search_word) && search_word !== '');



        if (keywords.length > 0) {
            const keyword_search: KeywordSearch = {
                operator: CombineOperator.Or,
                keywords,
                sub_searches: []
            };
            searchFilter = {
                keyword_search
            };
        }

        this.mapsForYou = await getTopics(BackpackPage.amplitude_callsite, searchFilter) ?? [];
        this.isMapsForYouLoading = false;
    }

    private startMapClick() {
        window.location.assign('/#template-onboard?from=backpack');
    }

    private async aiMapBuild() {
        if (this.currentTabIndex === 0) {
            await this.textToMap();
        // } else if (this.currentTabIndex === 1) {
        //     await this.promptToMap();
        }
    }

    private async textToMap() {
        // check if AI eligible
        if (!this.ai_eligible) {
            logWithAmplitude(BackpackPage.amplitude_callsite, 'AI Not Eligible', {
                email: accountInfo.email,
            });
            return;
        }

        // get text area value and check that it isnt empty
        const ttm_text = this.textToMapAreaElement?.value;

        if (ttm_text === undefined || ttm_text === '' || ttm_text === null) {
            return;
        }

        this.ttmIsLoading = true;

        try {
            // create topic
            const ttm = await textToMapAI(
                BackpackPage.amplitude_callsite,
                ttm_text,
                organizationInfo?.id?.$oid ?? undefined
            );
            this.ttmIsLoading = false;

            // check if ttm_topic is defined and has a cc_account_admin_link property
            if (isDefined(ttm) && ttm?.cc_account_admin_link) {
                window.location.href = ttm.cc_account_admin_link;
            }
        } catch (error) {
            console.error('Failed to create topic');
            this.ttmIsLoading = false;
            this.ttmError = true;
            if (this.textToMapAreaElement !== undefined && this.textToMapAreaElement !== null) {
                this.textToMapAreaElement.value = ttm_text;
            }
        }
    }

    private async openCustomInsightsModal(e: CustomEvent | any) { // this is a very expensive call to load the UI bakery info.  ensure that it isn't loading unless clicked.
        if (featuresInfo.advancedInsightsEnabled) {
            this.showAdvancedInsightsModal = true;
            await this.updateComplete;
            if (isDefined(this.customAdvancedInsightsModal)) {
                this.customAdvancedInsightsModal.open = true;
            }
            logWithAmplitude(BackpackPage.amplitude_callsite, `All Up Advanced Insights Load`, {
                email: accountInfo.email,
            });
        } else {
            this.openUpgradeDialog(FeatureType.AdvancedInsights);
        }
        logWithAmplitude(BackpackPage.amplitude_callsite, `All Up Advanced Insights Click`, {
            email: accountInfo.email,
        });
    }

    private renderContent() {
        if (this.filterType !== undefined) {
            return html`
                <div class="backpack-list-header">
                    <div class="buttonContainer">
                        <mwc-icon-button
                            class="view-type-btn ${this.viewType === 'list' ? 'btn-active' : ''}"
                            icon="list"
                            @click=${() => { this.viewType = 'list'; }}
                        ></mwc-icon-button>
                        <mwc-icon-button
                            class="view-type-btn ${this.viewType === 'cards' ? 'btn-active' : ''}"
                            icon="dashboard"
                            @click=${() => { this.viewType = 'cards'; }}
                        ></mwc-icon-button>
                    </div>
                </div>
                <div class="backpack-list-section">
                    <backpack-cards
                        @on-update-topics=${this.onUpdateTopics}
                        @on-lock-click=${() => this.openUpgradeDialog(FeatureType.Backpack)}
                        .folders=${this.folders}
                        .isBackpackPage="${true}"
                        .viewType=${this.viewType}
                        .topics=${getFilteredTopics(
                            this.filterType,
                            this.topics,
                            this.personal_topics,
                            this.contributedTopics,
                            this.bookmarks,
                            accountInfo.email,
                        )}
                    >
                    </backpack-cards>
                </div>
            `;
        }
        const dashboardtopics = getFilteredTopics(FilterType.AllMaps, this.topics, this.personal_topics, this.contributedTopics, this.bookmarks, accountInfo.email) ?? [];
        return html`
            ${organizationInfo.organization?._id.$oid === '681e397ed53ddcf087ac8e3f' ? '' : html`
                <div class="backpack__map-section">
                    <div class="backpack__map-section-title-wrapper">
                        <h3 class="backpack__map-section-title">Recent</h3>
                        ${dashboardtopics.length > 4 ? html`
                            <div
                                class="backpack__map-section-link"
                                @click=${() => {this.filterType = 'All';}}
                                >
                                View All Maps
                            </div>
                        ` : ''}
                        ${dashboardtopics.length === 0 && !this.isBackpackMapsLoading ? html`
                            <span class="backpack__map-section-empty">You have not created your own maps in this workspace yet.</span>
                        ` : ''}
                    </div>
                    ${this.isBackpackMapsLoading ? html`
                            <div class="loadingImage">
                                <img src="/images/backpack/ic_loading.gif"/>
                            </div>
                        ` : html`
                        <div class="backpack-list-section">
                            <backpack-cards
                                @on-lock-click=${() => this.openUpgradeDialog(FeatureType.Backpack)}
                                @isBackpackPage="${true}"
                                @on-update-topics=${this.onUpdateTopics}
                                .viewType=${'cards-part'}
                                .folders=${this.folders}
                                .topics=${dashboardtopics.slice(0, 12)}>
                            </backpack-cards>
                        </div>
                        ${this.bookmarks.length > 0 ? html`
                            <div class="backpack__map-section-title-wrapper">
                                <h3 class="backpack__map-section-title">Your Bookmarks</h3>
                                ${this.bookmarks.length > 12 ? html`
                                    <div
                                        class="backpack__map-section-link"
                                        @click=${() => {this.filterType = 'BookmarkedMaps';}}
                                    >
                                        View All Bookmarks
                                    </div>
                                ` : ''}
                            </div>
                            <div class="backpack-list-section">
                                <backpack-cards
                                    @on-lock-click=${() => this.openUpgradeDialog(FeatureType.Backpack)}
                                    @on-update-topics=${this.onUpdateTopics}
                                    .viewType=${'cards-part'}
                                    .folders=${this.folders}
                                    .topics=${this.bookmarks.slice(0, 12)}>
                                </backpack-cards>
                            </div>
                        ` : ''}
                    `}
                </div>
            `}
            <mwc-dialog id="upgradeDialogComponent" class="dialog" hideActions>
                <modal-dialog size="md" @close="${() => this.upgradeDialogComponent.close()}">
                    <upgrade-dialog
                        slot="body"
                        .featureType="${this.featureType}"
                        @close="${() => this.upgradeDialogComponent.close()}"
                    >
                    </upgrade-dialog>
                </modal-dialog>
            </mwc-dialog>`;
    }

    private renderMetrics() {
        if (organizationInfo.isGuest || this.insightTopics.length === 0 || this.isInsightsLoading) {
            return html``;
        }
        return html`
            <div class="backpack-list-section">
                <div class="insightsSection">
                    ${this.isInsightsLoading ? html`
                        <div class="loadingImage">
                            <loading-element
                                .loadingString=${'Loading Maps & Insights...'}
                                .horizontal=${true}>
                            </loading-element>
                        </div>
                            `: html`
                        <insight-cards
                            class="insightsSection__wrapper"
                            @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDialog(e.detail.feature_type)}
                            @open-full-insights=${async (e: CustomEvent) => await this.openCustomInsightsModal(e)}
                            .topics=${this.insightTopics}
                            .isLoading=${this.isInsightsLoading}
                            >
                        </insight-cards>
                    `}
                </div>
            </div>
        `;
    }

    private renderMapGenerator() {
        return html`
            <div class="backpack__create_map_section">
                <div class="backpack__generator_title">
                    <h1>Create a Map</h1>
                    <div class="backpack__generator_subtitle">
                        <span>AI Beta</span>
                        <mwc-icon>auto_awesome</mwc-icon>
                    </div>
                </div>
                <div class="backpack__generator_create_btn">
                    <form-button
                        variant="outline"
                        size="sm"
                        id="createMapBtn"
                        @click=${this.startMapClick}
                    >
                        Create from Scratch
                    </form-button>
                </div>
                <div class="backpack__create_map_section_generator">
                    ${this.ttmIsLoading ? html`
                        <div class="loadingImage">
                            <img src="/images/loading_running.gif"/>
                        </div>
                    ` : html`
                        <tabs-component
                            .tabIndex=${this.currentTabIndex}
                            @on-select-tab=${(e: any) => { this.currentTabIndex = e.detail.tabIndex; }}
                            .options=${['From Text']}>
                        </tabs-component>
                        <div class="backpack__generator_content" @click="${!this.ai_eligible ? () => this.openUpgradeDialog(FeatureType.NumberOfUsesOfProxiAI) : ''}">
                            <span class="error-message-ttm">${this.ttmError ? `Oh no, we didn't get that one quite right. Try again.` : ''}</span>
                            ${this.currentTabIndex === 0 ? html`
                                <mwc-textarea
                                    id="textToMapArea"
                                    outlined
                                    ?disabled=${!this.ai_eligible}
                                    label="What would you like to map?"
                                    placeholder="Paste text from blog, text message, website, video script, document, itinerary, instagram caption... anything!"
                                    charCounter="internal"
                                    maxLength="8000"
                                    rows="3"
                                    style="width: 100%;--mdc-text-field-outlined-idle-border-color:transparent; --mdc-typography-subtitle1-font-size: 12px;">
                                </mwc-textarea>
                            ` : ''}
                            ${this.currentTabIndex === 1 ? html`
                                <mwc-textarea
                                    id="promptArea"
                                    outlined
                                    ?disabled=${!this.ai_eligible}
                                    label="What kind of map would you like to build?"
                                    placeholder="Be as creative as you wish! Day dates in Austin, Texas with a dog? Kid friendly hikes in Denver, Colorado? Places to casually run into a billionare in the United States? We got you."
                                    charCounter="internal"
                                    maxLength="400"
                                    rows="3"
                                    style="width: 100%;--mdc-text-field-outlined-idle-border-color:transparent; --mdc-typography-subtitle1-font-size: 12px;"
                                ></mwc-textarea>
                                ` : ''}
                        </div>
                        <div class="backpack__generator_footer">
                            <div class="backpack__generator_footer_info">
                                <span>${accountInfo.ai_tool_uses_this_month ?? 0} / ${featuresInfo.numberOfUsesOfProxiAIEnabled} AI Calls Used this Month</span>
                                ${featuresInfo.duplicateMapEnabled ? '' : html`
                                | <span class="backpack__map-section-link" @click=${() => this.openUpgradeDialog(FeatureType.NumberOfUsesOfProxiAI)}> Upgrade for More </span>
                                `}
                            </div>
                            <div style="padding: 0.5rem">
                                <form-button
                                    variant="raised"
                                    size="sm"
                                    ?disabled=${!this.ai_eligible}
                                    @click=${!this.ai_eligible ? () => this.openUpgradeDialog(FeatureType.NumberOfUsesOfProxiAI) : async () => await this.aiMapBuild()}
                                >
                                    Generate Now
                                </form-button>
                            </div>
                        </div>`
                }
                </div>
            </div>
        `;
    }

    get wasOrganizationRecentlyCreated(): boolean {
        const creationTime = new Date(organizationInfo.organization?.created_at ?? '');
        if (isNaN(creationTime.getTime())) {
            return false;
        }
        // Considered "recently created" if within the last two months
        const currentTime = new Date();
        const twoMonthsAgo = new Date();
        twoMonthsAgo.setMonth(currentTime.getMonth() - 2);
        return creationTime > twoMonthsAgo;
    }

    render() {
        return html`
            <div class="pageContainer">
                ${this.runningMigration ? html`
                    <loading-element
                        .loadingString=${`Updating your account to our lastest features... please wait until you are redirected!`}
                    ></loading-element>
                ` :
                html`
                    <div class="sidebar">
                        <backpack-sidebar
                            id="sidebarComponent"
                            @on-select-backpack-filter-type=${this.onUpdateFilterType}
                            @update-page-name=${this.onUpdatePageNme}
                            @on-select-creator-profile=${this.setProfileId}
                            .showPersonalTopics=${this.personal_topics.length > 0}
                            .isLoading=${this.isBackpackMapsLoading}
                            .pageName=${this.currentPageName}
                            .topics=${this.topics}
                            .filterType=${this.filterType}
                            .showFolders=${!detectMobileViewport()}
                            .showCreatorProfiles=${!detectMobileViewport()}
                            .mobileMinified=${this.mobileMinified}
                            .showEmailDownload=${this.showEmailDownload}>
                        </backpack-sidebar>

                    </div>
                    ${detectMobileViewport() ? html`
                    <div class="sidebar-expand-icon">
                        <mwc-icon
                        @click=${() => {this.mobileMinified = !this.mobileMinified;}}
                            >
                            ${this.mobileMinified ? 'expand_more' : 'expand_less'}
                        </mwc-icon>
                    </div>
                        ` :
                        ''}
                    <div class="rightSideHolder">
                        ${this.showNewUiBanner ? html`
                            <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 16px;background:#FEF3C7;border:1px solid #FCD34D;border-radius:8px;margin:8px 16px 0;">
                                <span style="font-size:14px;color:#92400E;font-weight:500;">
                                    This page is being deprecated — please migrate to the new Proxi dashboard. If you run into any issues,
                                    <span
                                        style="color:#92400E;text-decoration:underline;cursor:pointer;font-weight:600;"
                                        @click=${() => openChatWindow(BackpackPage.amplitude_callsite, 'I need help migrating from the classic dashboard: ')}
                                    >reach out</span>.
                                </span>
                                <button
                                    style="padding:8px 16px;background:#92400E;color:#fff;border:none;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap;"
                                    @click=${() => {
                                        setUiPref('new');
                                        if (isCustomDomain()) {
                                            void redirectToNewUiWithTransfer();
                                        } else {
                                            window.location.href = getNewUiUrl();
                                        }
                                    }}
                                >Go to new dashboard</button>
                            </div>
                        ` : ''}
                        <div class="topActions">
                            <span id="currentPageName">${this.currentPageName}</span>
                            <div class="flexContainer">
                                ${organizationInfo.organization?.is_administrator === true ? html`
                                    <multi-user-avatar
                                        @click=${this.openTeammates}
                                        class="desktopOnly"
                                        .backgroundColor=${organizationInfo.organization?.primary_color}
                                        .textColor=${organizationInfo.organization?.secondary_color}
                                        .users=${organizationInfo.allUsersAsUserInfos}
                                        size="sm"
                                    >
                                    </multi-user-avatar>`
                                : ''}
                                ${accountInfo.isAppSumoUser === true ? html`
                                    <div class="subscriptionTag" @click=${this.openSubscription}>
                                        <div class="subscriptionNameTag">${subscriptionInfo.subscriptionLevelText}</div>
                                    </div>
                                ` : html`
                                    <div class="subscriptionTag" @click=${this.openSubscription}>
                                        <div class="subscriptionNameTag">${subscriptionInfo.subscriptionLevelText}</div>
                                    </div>
                                `}
                                <form-button
                                    size="xs"
                                    class="buttonSection"
                                    @click="${this.startMapClick}"
                                >
                                    <mwc-icon class="shareButtonIcon ${this.newMapLoading ? 'spin' : ''}">add</mwc-icon>
                                    New Map or Experience
                                </form-button>
                            </div>
                        </div>
                        <div class="mainContent">
                            ${this.loading ? html`
                                <loading-element horizontal loadingString="Loading your maps and experiences..." style="margin-bottom: 20px;"></loading-element>
                            ` : ''}
                            ${this.currentPageName === 'Team Settings' || this.currentPageName === 'Workspace Settings' ? html `${this.renderTeamSettings()}` : ''}
                            ${this.currentPageName === 'Account Settings' ? html `${this.renderAccountSettings()}` : ''}
                            ${this.currentPageName === 'Home' ? html `
                                ${this.wasOrganizationRecentlyCreated ? html `
                                    <onboarding-call-notification
                                        placement="backpack">
                                    </onboarding-call-notification>
                                ` : ''}
                                ${this.renderMetrics()}
                                ${this.renderContent()}
                                ` : ''}
                            ${this.currentPageName === 'Backpack' ? html `
                                ${this.renderContent()}
                                ` : ''}
                            ${this.currentPageName === 'Folder' ? html `
                                ${this.renderFolderPage()}
                                ` : ''}
                            ${this.currentPageName === 'Public Profile' ? html `
                                ${this.renderCreatorProfilePage()}
                                ` : ''}
                            ${this.currentPageName === 'Discover' ? html `
                                <discover-page hideHeader></discover-page>
                                ` : ''}
                        </div>
                    </div>
                `}
            </div>
            ${this.showAdvancedInsightsModal
            ? html`<mwc-dialog
                    id="customAdvancedInsightsModal"
                    class="dialog"
                    hideActions
                >
                    <modal-dialog
                        size="xl"
                        @close="${() => this.customAdvancedInsightsModal?.close()}"
                    >
                        <div slot="body" style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px; gap: 16px;">
                            <p style="font-size: 16px; color: #666;">Analytics have moved to the new dashboard.</p>
                            <a href="${getNewUiUrl()}"
                               target="_blank"
                               rel="noopener noreferrer"
                               style="padding: 12px 24px; background: #2563eb; color: white; border-radius: 8px; text-decoration: none; font-weight: 500;">
                                View Analytics in New Dashboard
                            </a>
                        </div>
                    </modal-dialog>
                </mwc-dialog>`
            : ''}
            <mwc-dialog id="organizationProfilePopupModal" class="dialog" hideActions>
                <modal-dialog size="md" @close="${() => this.closeOrganizationProfilePopup()}">
                    <organization-profile-popup
                        slot="body"
                        @close="${() => this.closeOrganizationProfilePopup()}"
                    ></organization-profile-popup>
                </modal-dialog>
            </mwc-dialog>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    renderTeamSettings() {
        return html`
            <team-settings-page @open-workspace-dropdown="${this.openWorkspaceDropdown}"></team-settings-page>
        `;
    }

    renderAccountSettings() {
        return html`
            <account-info-page></account-info-page>
        `;
    }

    renderFolderPage() {
        return html`
            <folder-page></folder-page>
        `;
    }

    renderCreatorProfilePage() {
        return html`
            ${isDefined(this.profileId) && this.profileId !== '' ? html`
                <edit-creator-profile-page .profileId="${this.profileId}" id="editCreatorProfilePage"
                > </edit-creator-profile-page>
            ` : ''}
        `;
    }

    static get styles() {
        return css`
            .pageContainer {
                display: flex;
                width: 100%;
                height: calc(100vh - ${unsafeCSS(heights.nav)});
                box-sizing: border-box;
                align-items: stretch;
            }

            .backpack__create_map_section {
                background-image: url('/images/gradient2.png');
                background-repeat: no-repeat;
                background-position: center;
                background-size: cover;
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 2rem;
                border-radius: 0.5rem;
                position: relative;
                box-shadow: 0px 4px 6px 0 #ECECEC;
            }

            .backpack__create_map_section h1 {
                font-size: 1.5rem;
                margin-top: 0;
                margin-bottom: 1rem;
            }

            .help-video {
                cursor:pointer; display:flex;
                flex-direction: row;
                align-items:center;
            }

            .templateSection__cta-header {
                display: flex;
                align-items: baseline;
                flex-direction: row;
                justify-content: space-between;
                width: 100%;
            }

            .backpack__create_map_section_generator {
                width: 100%;
                max-width: 700px;
                border-radius: 0.5rem;
                background: var(--theme-white-color);
                margin-bottom: 0.5rem;
            }

            .backpack__generator_create_btn {
                position: absolute;
                top: 1.25rem;
                right: 1.5rem;
            }

            .backpack__generator_title {
                display: flex;
                align-items: flex-start;
                gap: 10px;
            }

            .backpack__generator_subtitle {
                display: flex;
                align-items: center;
                font-weight: bold;
                gap: 6px;
            }

            .backpack__generator_content {
                border-top: 1px solid var(--theme-input-border-color);
                border-bottom: 1px solid var(--theme-input-border-color);
                padding: 0.75rem;
                color: var(--theme-lighter-text-color);
            }

            .backpack__generator_footer {
                display: flex;
                align-items: center;
                justify-content: space-between;
                flex-wrap: wrap;
            }

            .backpack__generator_footer_info {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                padding: 0.5rem;
                margin-left: 0.5rem;
                color: var(--theme-lighter-text-color);
            }

            .backpack__map-section {
                padding: 0 2rem;
            }

            .backpack__map-section-title-wrapper {
                width: 100%;
                display: flex;
                justify-content: space-between;
                align-items: baseline;
                flex-wrap: wrap;
                gap: 5px 20px;
                margin: 10px 0 20px;
            }

            h3.backpack__map-section-title {
                color: #091E2C;
                text-align: left;
                font-size: 20px;
                font-weight: 700;
                line-height: 100%;
                margin: 0;
            }

            .backpack__map-section-link {
                color: var(--main-invalid-color);
                text-align: left;
                font-size: 12px;
                line-height: 130%;
                cursor: pointer;
                text-decoration: underline;
            }

            .bottomTabs {
                position: fixed;
                bottom: 0;
                width: 100%;
                box-sizing: border-box;
                align-items: center;
                justify-content: space-around;
                background: var(--main-secondary-color);
                color: var(--theme-white-color);
                z-index: 2;
            }

            .bottomTabs .tabItem {
                display: flex;
                flex: 1;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                padding: 0.5rem;
                color: var(--theme-white-color);
                border-top: 4px solid transparent;
                cursor: pointer;
                text-decoration: none;
            }

            .bottomTabs .tabItem.active {
                color: var(--main-primary-color);
                border-top: 4px solid var(--main-primary-color);
            }

            .sidebar {
                border-right: 1px solid #ECECEC;
                box-sizing: border-box;
                height: 100vh;
                box-shadow: 1px 0 2px 0 rgba(0,0,40,.08),6px 0 10px 0 rgba(0,0,40,.08);
                background-color: white;
                border-radius: 0 8px 8px 0;
            }

            .mainContent {
                flex: 1;
                width: 100%;
                padding: 1.5rem;
                box-sizing: border-box;
                overflow-y: auto;
            }

            .rightSideHolder {
                flex: 1;
                width: calc(100vw - 360px);
                box-sizing: border-box;
                height: 100vh;
                display: flex;
                flex-direction: column;
            }

            .actionButtons {
                display: flex;
                margin-top: 2rem;
                justify-content: space-between;
            }

            .loadingImage {
                display: flex;
                justify-content: center;
                max-height: 200px;
            }

            .insightsSection__wrapper {
                flex: 3 1 0%;
            }

            .error-message-ttm {
                color: var(--main-invalid-color);
                font-size: 0.75rem;
                margin-bottom: 0.5rem;
            }


            .insightsSection__cta, .templateSection {
                flex: 1;
                display: flex;
                padding: 1rem 2rem;
                flex-direction: column;
                justify-content: space-between;
                align-items: flex-start;
                border-radius: 12px;
                background: #F7F7F7;
            }

            .templateSection {
                margin-bottom: 20px;
            }

            .insightsSection__cta-header {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 16px;
            }

            .insightsSection__cta-header > a > img {
                width: 79px;
                height: 79px;
                cursor: pointer;
            }

            .insightsSection__cta-header > h1 {
                color: #1D1B20;
                font-size: 20px;
                font-weight: 700;
                line-height: 130%;
                margin: 0;
                cursor: default;
            }

            .insightsSection__cta-header > h1 > a {
                color: var(--main-invalid-color);
                text-decoration: none;
                cursor: pointer;
            }

            .insightsSection__cta-link {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 8px;
                text-decoration: none;
                cursor: pointer;
                color: var(--main-invalid-color);
            }

            .insightsSection__cta-link > span {
                color: var(--main-invalid-color);
                text-align: left;
                font-size: 16px;
                font-weight: 700;
                line-height: 150%;
            }

            .insightsSection__cta-link > img {
                width: 24px;
                height: 24px;
            }

            .insightsSection {
                width: 100%;
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                flex-wrap: wrap;
                gap: 1rem 2rem;
                margin-bottom: 2rem;
            }

            .backpack-list-title {
                margin: 0;
                color: var(--main-invalid-color);
                font-size: 16px;
                font-weight: 700;
                display: inline-block;
                cursor: pointer;
            }

            .backpack-list-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            .buttonContainer {
                --mdc-icon-button-size: 2rem;
                --mdc-icon-size: 1rem;
            }

            .buttonContainer .view-type-btn {
                color: var(--main-moon-color);
            }

            .buttonContainer .view-type-btn.btn-active {
                color: var(--main-invalid-color);
            }

            .backpack-list-section {
                margin-bottom: 20px;
            }

            .visited-places-list-section {
                width: 100%;
                padding-bottom: 1.5rem;
                box-sizing: border-box;
                margin: 0px -10px;
                display: flex;
                flex-wrap: wrap;
                overflow-y: auto;
            }

            .visited-places-list-section .column-item {
                width: 33%;
                min-width: 150px;
                padding: 10px;
                box-sizing: border-box;
            }

            .topActions {
                display: flex;
                justify-content: space-between;
                align-items: center;
                border-bottom: 1px solid var(--theme-border-color);
                padding: 0.5rem 2rem;
            }

            .flexContainer {
                display: flex;
                gap: 20px;
                flex-direction: row;
                align-items: center;
            }

            setting-drawer {
                --bottom-height: 0px;
            }

            @media (min-width: 551px) {
                .desktopOnly {
                    display: block;
                }

                .mobileOnly {
                    display: none;
                }
            }

            @media (max-width: 550px) {
                .desktopOnly {
                    display: none;
                }

                .mobileOnly {
                    display: block;
                }

                .sidebar-expand-icon {
                    display: flex;
                    justify-content: center;
                }

                .bottomTabs.mobileOnly {
                    display: flex;
                }

                .pageContainer {
                    flex-direction: column;
                    height: 100vh;
                    padding: 20px;
                }

                .topActions {
                    padding: 0.5rem;
                }

                .rightSideHolder {
                    width: unset;
                }

                .sidebar {
                    width: 100%;
                    height: auto;
                    border-right: none;
                    border-bottom: 1px solid #ECECEC;
                }

                .mainContent {
                    padding: 0rem 0rem 3rem;
                }

                .backpack__map-section {
                    padding: 1rem;
                }

                h3.backpack__map-section-title {
                    font-size: 1.2rem;
                }

                .insightsSection__cta-header > h1 {
                    font-size: 1.2rem;
                }

                .backpack__create_map_section {
                    padding: 1rem;
                }

                .backpack__create_map_section h1 {
                    font-size: 1rem;
                }

                .backpack__generator_title {
                    margin-top: 2.5rem;
                }

                .backpack__generator_create_btn {
                    top: 10px;
                    right: 10px;
                }

                .visited-places-list-section .column-item {
                    width: 100%;
                }
            }
        `;
    }
}
